import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  TextField,
  Autocomplete,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Add, LocalOffer } from '@mui/icons-material';
import { useTag } from 'app/context/TagContext';

interface TagSelectorProps {
  value: string[]; // Array of selected tag names/IDs
  onChange: (tags: string[]) => void;
  tagType?: string; // Type to filter tags (e.g., 'CTA_BUTTON')
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  value = [],
  onChange,
  tagType = 'CTA_BUTTON',
  label = 'Tags',
  placeholder = 'Type to search or add new tags...',
  disabled = false,
  maxTags = 10,
}) => {
  const { tags, loading, getTags, createTag } = useTag();
  const [inputValue, setInputValue] = useState('');
  const [filteredTags, setFilteredTags] = useState<any[]>([]);

  // Fetch tags by type on mount
  useEffect(() => {
    const fetchTagsByType = async () => {
      try {
        await getTags({ type: tagType });
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };

    fetchTagsByType();
  }, [tagType, getTags]);

  // Filter tags based on input
  useEffect(() => {
    if (!tags) return;

    const available = tags.filter(tag => 
      tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
      !value.includes(tag.name)
    );
    setFilteredTags(available);
  }, [tags, inputValue, value]);

  const handleTagChange = async (event: any, newValue: string[]) => {
    if (newValue.length > maxTags) return;

    // Check if last added value is a new tag (not in existing tags)
    const lastValue = newValue[newValue.length - 1];
    if (lastValue && !tags.some(tag => tag.name === lastValue)) {
      // Create new tag
      try {
        await createTag({
          name: lastValue,
          type: tagType,
        });
      } catch (error) {
        console.error('Error creating new tag:', error);
      }
    }

    onChange(newValue);
  };

  const getOptionLabel = (option: string | any) => {
    if (typeof option === 'string') return option;
    return option.name || '';
  };

  const renderOption = (props: any, option: any, { selected }: any) => (
    <Box component="li" {...props}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <LocalOffer fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ flex: 1 }}>
          {typeof option === 'string' ? option : option.name}
        </Typography>
        {!tags.some(tag => tag.name === (typeof option === 'string' ? option : option.name)) && (
          <Chip
            size="small"
            label="New"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        )}
      </Box>
    </Box>
  );

  const renderTags = (tagValue: string[], getTagProps: any) =>
    tagValue.map((option: string, index: number) => (
      <Chip
        {...getTagProps({ index })}
        key={option}
        variant="outlined"
        label={option}
        size="small"
        color="primary"
        deleteIcon={<Box component="span">×</Box>}
        sx={{
          '& .MuiChip-deleteIcon': {
            fontSize: '18px',
            '&:hover': {
              color: 'error.main',
            },
          },
        }}
      />
    ));

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Autocomplete
        multiple
        freeSolo
        disabled={disabled || loading}
        options={filteredTags.map(tag => tag.name)}
        value={value}
        onChange={handleTagChange}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderTags={renderTags}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={value.length === 0 ? placeholder : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
              sx: {
                padding: '8px 14px',
                '& .MuiAutocomplete-tag': {
                  margin: '2px',
                },
                '& .MuiAutocomplete-input': {
                  minWidth: '100px !important',
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingTop: '8px',
                paddingBottom: '8px',
              },
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-tag': {
            marginRight: '4px',
            marginBottom: '2px',
          },
        }}
        filterOptions={(options, params) => {
          const filtered = options.filter(option =>
            option.toLowerCase().includes(params.inputValue.toLowerCase())
          );

          const { inputValue } = params;
          const isExisting = options.some(option => inputValue === option);
          if (inputValue !== '' && !isExisting) {
            filtered.push(inputValue);
          }

          return filtered;
        }}
        noOptionsText={
          inputValue ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <Add fontSize="small" />
              <Typography variant="body2">
                Press Enter to create "{inputValue}"
              </Typography>
            </Box>
          ) : (
            'No tags found'
          )
        }
      />
      {value.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {value.length}/{maxTags} tags selected
        </Typography>
      )}
    </Box>
  );
};

export default TagSelector; 